import { useState, useEffect } from "react";

const API_BASE =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  "http://127.0.0.1:5000";

const CONTACT_EMAIL = "info@guudvision.health";

const BRAND = {
  primary: "#9c27b0",
  primaryDark: "#6a0080",
  primaryLight: "#ce93d8",
  primaryBg: "#f3e5f7",
  white: "#ffffff",
  text: "#1a1a1a",
  textLight: "#666",
  border: "#e0d4e8",
  bg: "#faf5fc",
};

const PROVINCES = [
  "Eastern Cape","Free State","Gauteng","KwaZulu-Natal",
  "Limpopo","Mpumalanga","North West","Northern Cape","Western Cape",
];
const SCRUB_SIZES = ["XS","S","M","L","XL","XXL"];
const POSITIONS = ["Optometrist","Optical Dispenser","Other"];

const portalToken = () => localStorage.getItem("applicantToken") || "";

// Same basename logic as App.js — deployed under /talentpool in production.
const BASENAME = window.__RUNTIME_CONFIG__?.apiBase?.startsWith("/talentpool") ? "/talentpool" : "";

// The 6-stage onboarding checklist that starts once the Offer Letter is signed.
const ONBOARDING_STAGES = [
  { key: "team_member_info", label: "Team Member Info" },
  { key: "criminal_check", label: "Criminal Check" },
  { key: "fixed_term_contract", label: "Fixed-Term Contract" },
  { key: "pledge_confidentiality", label: "Pledge of Confidentiality" },
  { key: "cellphone_policy", label: "Cellphone Policy" },
  { key: "complete", label: "Complete" },
];

const inp = {
  padding: "10px 12px",
  border: `1px solid ${BRAND.border}`,
  borderRadius: 8,
  fontSize: 14,
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  fontFamily: "inherit",
};

const lbl = {
  display: "flex",
  flexDirection: "column",
  fontSize: 13,
  color: BRAND.textLight,
  gap: 5,
  marginBottom: 12,
};

const btnP = (disabled) => ({
  padding: "12px 24px",
  background: disabled ? "#ccc" : BRAND.primary,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: disabled ? "not-allowed" : "pointer",
  fontWeight: 700,
  fontSize: 15,
  width: "100%",
  fontFamily: "inherit",
});

const btnS = {
  padding: "12px 24px",
  background: "#fff",
  color: BRAND.primary,
  border: `2px solid ${BRAND.primary}`,
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 15,
  width: "100%",
  fontFamily: "inherit",
};

const STATUS_DISPLAY = {
  "Under review":          { label: "Application Under Review",        color: "#9c27b0", bg: "#f3e5f7", icon: "🔍" },
  "Short listed":          { label: "You Have Been Short Listed!",      color: "#2e7d32", bg: "#f1f8e9", icon: "⭐" },
  "Offer Letter Sent":     { label: "Offer Letter Has Been Sent",       color: "#1565c0", bg: "#e3f2fd", icon: "📩" },
  "Offer Letter Accepted": { label: "Offer Letter Accepted!",           color: "#2e7d32", bg: "#e8f5e9", icon: "🎉" },
  "Placed":                { label: "You Are Currently Placed",         color: "#00695c", bg: "#e0f2f1", icon: "✅" },
  "Rejected":              { label: "Application Not Successful",       color: "#b71c1c", bg: "#ffebee", icon: "❌" },
};

function extractError(data, fallback) {
  const d = data?.detail ?? data?.error ?? data?.message;
  if (typeof d === "string" && d.trim()) return d;
  return fallback;
}

// ─── POPIA Consent Modal ──────────────────────────────────────────────────────

const POPIA_SECTIONS = [
  { num: 1, title: "Who is collecting your information?",
    body: "Guud Vision (Pty) Ltd is the responsible party under Section 1 of POPIA. We take full accountability for ensuring your personal information is collected and processed lawfully, securely, and only for stated purposes." },
  { num: 2, title: "What information do we collect?",
    body: "In line with the principle of minimality (Section 10), we only collect what is necessary:",
    bullets: ["Full name and contact details","Identity or passport number","CV, qualifications and certifications","Employment history and professional experience","Skills, competencies and areas of expertise"] },
  { num: 3, title: "Why are we processing your information?",
    body: "As required by Section 13, your information is used only for:",
    bullets: ["Storing your profile in the Guud Vision Talent Pool","Matching your profile to current and future opportunities","Contacting you when a suitable opportunity arises","Sharing your profile with relevant clients (with your consent)","Complying with applicable legal requirements"] },
  { num: 4, title: "How long do we keep your profile?",
    body: "Under Section 14, your Talent Pool profile will be retained for 3 years from submission, or until you request its removal. After this period your information will be securely deleted or de-identified." },
  { num: 5, title: "Will your profile be shared?",
    body: "Your profile may be shared with prospective employers or clients matching your profile. Any third-party processors are bound by Section 21 to process your data only under our instruction. Your information will not be sold." },
  { num: 6, title: "How do we keep your information secure?",
    body: "In compliance with Condition 7 (Sections 19–22), Guud Vision applies technical and organisational security measures to protect your profile. In the event of a security breach you will be notified as required by Section 22." },
  { num: 7, title: "Your rights as a data subject",
    body: "Under Condition 8 (Sections 23–25), you have the right to:",
    bullets: ["Access your personal information","Request correction of inaccurate details","Request removal of your profile at any time","Object to how your information is processed","Withdraw this consent at any time","Lodge a complaint with the Information Regulator"],
    footer: "Contact our Information Officer at privacy@guudvision.co.za" },
];

function POPIAModal({ onAccept, onDecline }) {
  const [open, setOpen] = useState({});
  const [chk1, setChk1] = useState(false);
  const [chk2, setChk2] = useState(false);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 560, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "1.4rem 1.6rem 1.1rem", borderBottom: "1px solid #eee", flexShrink: 0 }}>
          <span style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#0C447C", background: "#E6F1FB", padding: "3px 9px", borderRadius: 4, marginBottom: 10 }}>
            POPIA — Act 4 of 2013
          </span>
          <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.3 }}>
            Talent Pool — Personal Information Processing Consent
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: "#666", lineHeight: 1.6 }}>
            Before registering, please read how Guud Vision collects and uses your personal information.
          </p>
        </div>

        {/* Sections */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {POPIA_SECTIONS.map((s) => (
            <div key={s.num} style={{ borderBottom: "1px solid #f2f2f2" }}>
              <button onClick={() => setOpen((p) => ({ ...p, [s.num]: !p[s.num] }))}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "0.9rem 1.6rem", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                <span style={{ width: 24, height: 24, minWidth: 24, borderRadius: "50%", background: "#f4f4f4", border: "1px solid #e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#555" }}>{s.num}</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{s.title}</span>
                <span style={{ fontSize: 10, color: "#bbb", transform: open[s.num] ? "rotate(180deg)" : "none", transition: "transform 0.18s" }}>▼</span>
              </button>
              {open[s.num] && (
                <div style={{ padding: "0 1.6rem 1rem 3.6rem", fontSize: 13, color: "#555", lineHeight: 1.65 }}>
                  <p style={{ margin: "0 0 6px" }}>{s.body}</p>
                  {s.bullets && (
                    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                      {s.bullets.map((b, i) => (
                        <li key={i} style={{ padding: "2px 0 2px 14px", position: "relative" }}>
                          <span style={{ position: "absolute", left: 0, color: "#bbb" }}>–</span>{b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.footer && <p style={{ margin: "8px 0 0", fontStyle: "italic", color: "#777" }}>{s.footer}</p>}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "1.2rem 1.6rem", background: "#fafafa", borderTop: "1px solid #eee", flexShrink: 0 }}>
          <p style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>
            Do you consent to Guud Vision adding you to the Talent Pool?
          </p>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={chk1} onChange={(e) => setChk1(e.target.checked)} style={{ width: 15, height: 15, minWidth: 15, marginTop: 2, cursor: "pointer" }} />
            <span style={{ fontSize: 12, color: "#555", lineHeight: 1.55 }}>I have read and understood how Guud Vision will collect, store, use, and share my personal information in the Talent Pool, in accordance with POPIA (Act 4 of 2013).</span>
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16, cursor: "pointer" }}>
            <input type="checkbox" checked={chk2} onChange={(e) => setChk2(e.target.checked)} style={{ width: 15, height: 15, minWidth: 15, marginTop: 2, cursor: "pointer" }} />
            <span style={{ fontSize: 12, color: "#555", lineHeight: 1.55 }}>I give my voluntary, specific, and informed consent to Guud Vision processing and retaining my personal information for the purposes stated above. I understand I may withdraw this consent at any time.</span>
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onAccept} disabled={!(chk1 && chk2)}
              style={{ flex: 1, padding: "11px 0", background: chk1 && chk2 ? BRAND.primary : "#ddd", color: chk1 && chk2 ? "#fff" : "#999", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: chk1 && chk2 ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
              Accept &amp; Continue to Registration
            </button>
            <button onClick={onDecline} style={{ padding: "11px 16px", background: "transparent", color: "#777", border: "1px solid #ddd", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Decline</button>
          </div>
          <p style={{ margin: "10px 0 0", fontSize: 10, color: "#bbb", textAlign: "center" }}>Protection of Personal Information Act 4 of 2013 · Information Regulator (South Africa)</p>
        </div>
      </div>
    </div>
  );
}

// ─── Login Page ───────────────────────────────────────────────────────────────

function LoginPage({ onLogin, onGoRegister }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStep, setForgotStep] = useState(1);
  const [resetCode, setResetCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotErr, setForgotErr] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login/applicant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(extractError(data, "Login failed"));
      localStorage.setItem("applicantToken", data.access_token);
      localStorage.setItem("applicantUser", JSON.stringify({ name: data.user_name, email: data.user_email, role: data.role, employee_id: data.employee_id, folder_number: data.folder_number }));
      onLogin({ name: data.user_name, email: data.user_email, role: data.role });
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const sendReset = async (e) => {
    e.preventDefault(); setForgotErr(""); setForgotMsg(""); setForgotLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: forgotEmail.trim() }) });
      const data = await res.json();
      if (!res.ok) throw new Error(extractError(data, "Could not send reset code"));
      setForgotMsg(data.message || "Reset code sent — check your email.");
      setForgotStep(2);
    } catch (err) { setForgotErr(err.message); }
    finally { setForgotLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault(); setForgotErr("");
    if (newPass !== confirmPass) { setForgotErr("Passwords do not match."); return; }
    if (newPass.length < 8) { setForgotErr("Password must be at least 8 characters."); return; }
    setForgotLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: resetCode.trim(), new_password: newPass }) });
      const data = await res.json();
      if (!res.ok) throw new Error(extractError(data, "Reset failed"));
      setForgotMsg("Password reset! You can now sign in."); setShowForgot(false); setForgotStep(1);
    } catch (err) { setForgotErr(err.message); }
    finally { setForgotLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "system-ui, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: BRAND.primary, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><circle cx="14" cy="14" r="10" stroke="white" strokeWidth="2"/><path d="M9 14L12.5 17.5L19 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: BRAND.primary, letterSpacing: "-0.5px" }}>guud vision</div>
          <div style={{ fontSize: 12, color: BRAND.textLight, letterSpacing: "0.1em", textTransform: "uppercase" }}>Talent Pool Portal</div>
        </div>

        <div style={{ background: "#fff", borderRadius: 16, padding: 28, boxShadow: "0 4px 20px rgba(156,39,176,0.08)", border: `1px solid ${BRAND.border}` }}>
          {!showForgot ? (
            <>
              <h2 style={{ margin: "0 0 20px", fontSize: 20, fontWeight: 700, color: BRAND.text }}>Sign in</h2>
              <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
                <label style={lbl}>Email address<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inp} placeholder="you@email.com" /></label>
                <label style={lbl}>Password<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required style={inp} placeholder="••••••••" /></label>
                {error && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{error}</div>}
                <button type="submit" disabled={loading} style={btnP(loading)}>{loading ? "Signing in…" : "Sign In"}</button>
              </form>
              <button onClick={() => { setShowForgot(true); setForgotErr(""); setForgotMsg(""); }} style={{ marginTop: 12, background: "none", border: "none", color: BRAND.primary, fontSize: 13, cursor: "pointer", width: "100%", textAlign: "center" }}>Forgot password?</button>
              <div style={{ borderTop: `1px solid ${BRAND.border}`, marginTop: 20, paddingTop: 20 }}>
                <p style={{ margin: "0 0 12px", fontSize: 13, color: BRAND.textLight, textAlign: "center" }}>New to the Talent Pool?</p>
                <button onClick={onGoRegister} style={btnS}>Apply Now</button>
              </div>
            </>
          ) : (
            <>
              <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: BRAND.text }}>Reset Password</h2>
              <p style={{ margin: "0 0 20px", fontSize: 13, color: BRAND.textLight }}>{forgotStep === 1 ? "Enter your email to receive a reset code." : "Enter the code from your email and set a new password."}</p>
              {forgotStep === 1 ? (
                <form onSubmit={sendReset} style={{ display: "grid", gap: 12 }}>
                  <label style={lbl}>Email address<input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required style={inp} /></label>
                  {forgotErr && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{forgotErr}</div>}
                  {forgotMsg && <div style={{ background: "#f1f8e9", color: "#2e7d32", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{forgotMsg}</div>}
                  <button type="submit" disabled={forgotLoading} style={btnP(forgotLoading)}>{forgotLoading ? "Sending…" : "Send Reset Code"}</button>
                </form>
              ) : (
                <form onSubmit={doReset} style={{ display: "grid", gap: 12 }}>
                  <label style={lbl}>Reset code<input value={resetCode} onChange={(e) => setResetCode(e.target.value)} required style={inp} /></label>
                  <label style={lbl}>New password<input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} required minLength={8} style={inp} /></label>
                  <label style={lbl}>Confirm new password<input type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} required minLength={8} style={inp} /></label>
                  {forgotErr && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{forgotErr}</div>}
                  {forgotMsg && <div style={{ background: "#f1f8e9", color: "#2e7d32", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{forgotMsg}</div>}
                  <button type="submit" disabled={forgotLoading} style={btnP(forgotLoading)}>{forgotLoading ? "Resetting…" : "Reset Password"}</button>
                </form>
              )}
              <button onClick={() => { setShowForgot(false); setForgotStep(1); }} style={{ marginTop: 12, background: "none", border: "none", color: BRAND.primary, fontSize: 13, cursor: "pointer", width: "100%", textAlign: "center" }}>← Back to Sign In</button>
            </>
          )}
        </div>
        <p style={{ textAlign: "center", marginTop: 16, fontSize: 12, color: BRAND.textLight }}>
          <a href="/portal/how-it-works" style={{ color: BRAND.primary, fontWeight: 600 }}>What to Expect — how the process works</a>
        </p>
        <p style={{ textAlign: "center", marginTop: 6, fontSize: 12, color: BRAND.textLight }}>Questions? <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: BRAND.primary }}>{CONTACT_EMAIL}</a></p>
      </div>
    </div>
  );
}

// ─── Register Page (6-step wizard) ───────────────────────────────────────────

const STEPS = ["Account","Personal","Professional","Location","Documents","Review"];

function RegisterPage({ onRegistered, onGoLogin }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userId, setUserId] = useState(null);
  const [token, setToken] = useState("");

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", confirm_password: "",
    id_number: "", date_of_birth: "", gender: "",
    phone: "", address: "", city: "", province: "", postal_code: "", relocation_preferences: "",
    position_applied: "", hpcsa_number: "", years_experience: "", current_employer: "",
    employment_type: "", hourly_rate: "",
    notice_period: "", salary_expectation: "", scrub_size: "", available_from: "",
    additional_info: "",
  });
  const [docs, setDocs] = useState({ cv: null, id: null, degree: null, hpcsa: null });
  const [uploadStatus, setUploadStatus] = useState({});
  const [popiaConsent, setPopiaConsent] = useState(false);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const nextStep = async () => {
    setError("");
    if (step === 0) {
      if (!form.first_name || !form.last_name || !form.email || !form.password) { setError("Please fill in all required fields."); return; }
      if (form.password !== form.confirm_password) { setError("Passwords do not match."); return; }
      if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
      if (!popiaConsent) { setError("Please confirm you consent to your personal information being processed before continuing."); return; }
      // Register account
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/auth/register`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ first_name: form.first_name, last_name: form.last_name, email: form.email.trim(), password: form.password, role: "applicant", popia_consent: popiaConsent, popia_consent_at: new Date().toISOString() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(extractError(data, "Registration failed"));
        // Auto-login
        const loginRes = await fetch(`${API_BASE}/api/auth/login/applicant`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: form.email.trim(), password: form.password }),
        });
        const loginData = await loginRes.json();
        if (!loginRes.ok) throw new Error(extractError(loginData, "Login failed"));
        setToken(loginData.access_token);
        setUserId(loginData.employee_id);
        localStorage.setItem("applicantToken", loginData.access_token);
        localStorage.setItem("applicantUser", JSON.stringify({ name: loginData.user_name, email: loginData.user_email, role: loginData.role }));
      } catch (err) { setError(err.message); setLoading(false); return; }
      setLoading(false);
    }
    setStep((p) => p + 1);
  };

  const saveProfile = async () => {
    setLoading(true); setError("");
    try {
      const payload = {};
      ["phone","address","city","province","postal_code","id_number","date_of_birth","gender",
       "position_applied","hpcsa_number","years_experience","current_employer",
       "employment_type","hourly_rate","notice_period",
       "salary_expectation","scrub_size","available_from","relocation_preferences","additional_info"
      ].forEach((f) => { if (form[f]) payload[f] = form[f]; });
      await fetch(`${API_BASE}/api/applicants/profile`, {
        method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token || portalToken()}` },
        body: JSON.stringify(payload),
      });
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  };

  const handleDocUpload = async (type, file) => {
    if (!file) return;
    setDocs((p) => ({ ...p, [type]: file }));
    setUploadStatus((p) => ({ ...p, [type]: "uploading" }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", type);
      const res = await fetch(`${API_BASE}/api/documents/upload`, {
        method: "POST", headers: { Authorization: `Bearer ${token || portalToken()}` }, body: fd,
      });
      setUploadStatus((p) => ({ ...p, [type]: res.ok ? "done" : "error" }));
    } catch { setUploadStatus((p) => ({ ...p, [type]: "error" })); }
  };

  const finish = async () => {
    await saveProfile();
    const u = JSON.parse(localStorage.getItem("applicantUser") || "{}");
    onRegistered(u);
  };

  const progress = ((step) / (STEPS.length - 1)) * 100;

  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "system-ui, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 520 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: BRAND.primary, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" stroke="white" strokeWidth="1.8"/><path d="M8 12L11 15L16 9.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: BRAND.primary }}>guud vision</div>
          <div style={{ fontSize: 11, color: BRAND.textLight, letterSpacing: "0.1em", textTransform: "uppercase" }}>Talent Pool Application</div>
        </div>

        {/* POPIA consent confirmed badge */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 16, fontSize: 12, color: "#2e7d32", background: "#f1f8e9", border: "1px solid #a5d6a7", borderRadius: 8, padding: "8px 14px" }}>
          <span>✓</span> POPIA consent recorded
        </div>

        {/* Progress */}
        <div style={{ background: "#fff", borderRadius: 16, padding: 28, boxShadow: "0 4px 20px rgba(156,39,176,0.08)", border: `1px solid ${BRAND.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            {STEPS.map((s, i) => (
              <span key={s} style={{ fontSize: 10, fontWeight: i === step ? 700 : 400, color: i <= step ? BRAND.primary : "#ccc", textTransform: "uppercase", letterSpacing: "0.04em" }}>{s}</span>
            ))}
          </div>
          <div style={{ height: 4, background: "#f0e6f7", borderRadius: 2, marginBottom: 24 }}>
            <div style={{ height: "100%", width: `${progress}%`, background: BRAND.primary, borderRadius: 2, transition: "width 0.3s" }} />
          </div>

          {/* Step 0 — Account */}
          {step === 0 && (
            <div>
              <h3 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 700, color: BRAND.text }}>Create your account</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={lbl}>First name *<input value={form.first_name} onChange={set("first_name")} required style={inp} /></label>
                <label style={lbl}>Last name *<input value={form.last_name} onChange={set("last_name")} required style={inp} /></label>
              </div>
              <label style={lbl}>Email address *<input type="email" value={form.email} onChange={set("email")} required style={inp} /></label>
              <label style={lbl}>Password (min 8 chars) *<input type="password" value={form.password} onChange={set("password")} required style={inp} /></label>
              <label style={lbl}>Confirm password *<input type="password" value={form.confirm_password} onChange={set("confirm_password")} required style={inp} /></label>

              <div style={{ marginTop: 16, padding: 14, background: "#f5f7fa", border: "1px solid #dde3ea", borderRadius: 8, fontSize: 12.5, lineHeight: 1.6, color: "#4a5568" }}>
                <strong style={{ color: BRAND.text }}>Protection of Personal Information (POPIA)</strong>
                <p style={{ margin: "6px 0 0" }}>
                  By registering, you're giving Guud Vision your name, contact details, and — as you
                  complete this application — identity, qualification and employment information. We
                  use this information only to assess your application and, if you're placed, to manage
                  your employment. It won't be sold or shared outside this process except where the law
                  requires it or you separately agree (for example, when consenting to a criminal record
                  check later in the process).
                </p>
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, fontSize: 13, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={popiaConsent}
                  onChange={(e) => setPopiaConsent(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <span>I have read the above and consent to Guud Vision processing my personal information for this application. *</span>
              </label>
            </div>
          )}

          {/* Step 1 — Personal */}
          {step === 1 && (
            <div>
              <h3 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 700, color: BRAND.text }}>Personal details</h3>
              <label style={lbl}>ID / Passport number<input value={form.id_number} onChange={set("id_number")} style={inp} /></label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={lbl}>Date of birth<input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} style={inp} /></label>
                <label style={lbl}>Gender<select value={form.gender} onChange={set("gender")} style={inp}><option value="">— Select —</option><option>Male</option><option>Female</option></select></label>
              </div>
              <label style={lbl}>Phone number<input type="tel" value={form.phone} onChange={set("phone")} style={inp} /></label>
            </div>
          )}

          {/* Step 2 — Professional */}
          {step === 2 && (
            <div>
              <h3 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 700, color: BRAND.text }}>Professional details</h3>
              <label style={lbl}>Position applied for *<select value={form.position_applied} onChange={set("position_applied")} style={inp}><option value="">— Select —</option>{POSITIONS.map((p) => <option key={p}>{p}</option>)}</select></label>
              <label style={lbl}>HPCSA number<input value={form.hpcsa_number} onChange={set("hpcsa_number")} style={inp} /></label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={lbl}>Years experience<input type="number" value={form.years_experience} onChange={set("years_experience")} style={inp} min={0} /></label>
                <label style={lbl}>Scrub size<select value={form.scrub_size} onChange={set("scrub_size")} style={inp}><option value="">— Select —</option>{SCRUB_SIZES.map((s) => <option key={s}>{s}</option>)}</select></label>
              </div>
              <label style={lbl}>Employment type
                <select value={form.employment_type} onChange={set("employment_type")} style={inp}>
                  <option value="">— Select —</option>
                  <option value="permanent">Permanent Employment</option>
                  <option value="fixed_term">Fixed Term</option>
                  <option value="locum">Locum</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label style={lbl}>Current employer<input value={form.current_employer} onChange={set("current_employer")} style={inp} placeholder={form.employment_type === 'locum' ? 'Locum / agency' : ''} /></label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={lbl}>Notice period<input value={form.notice_period} onChange={set("notice_period")} style={inp} placeholder="e.g. 1 month" /></label>
                {form.employment_type === 'locum' ? (
                  <label style={lbl}>Rate per hour<input value={form.hourly_rate} onChange={set("hourly_rate")} style={inp} placeholder="e.g. R250/hr" /></label>
                ) : (
                  <label style={lbl}>Salary expectation<input value={form.salary_expectation} onChange={set("salary_expectation")} style={inp} placeholder="e.g. R25 000 pm" /></label>
                )}
              </div>
              <label style={lbl}>Available from<input type="date" value={form.available_from} onChange={set("available_from")} style={inp} /></label>
            </div>
          )}

          {/* Step 3 — Location */}
          {step === 3 && (
            <div>
              <h3 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 700, color: BRAND.text }}>Location</h3>
              <label style={lbl}>Street address<input value={form.address} onChange={set("address")} style={inp} /></label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={lbl}>City / Suburb<input value={form.city} onChange={set("city")} style={inp} /></label>
                <label style={lbl}>Postal code<input value={form.postal_code} onChange={set("postal_code")} style={inp} /></label>
              </div>
              <label style={lbl}>Province<select value={form.province} onChange={set("province")} style={inp}><option value="">— Select —</option>{PROVINCES.map((p) => <option key={p}>{p}</option>)}</select></label>
              <label style={lbl}>Relocation preferences<input value={form.relocation_preferences} onChange={set("relocation_preferences")} style={inp} placeholder="e.g. Open to relocate nationally" /></label>
            </div>
          )}

          {/* Step 4 — Documents */}
          {step === 4 && (
            <div>
              <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 700, color: BRAND.text }}>Upload documents</h3>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: BRAND.textLight }}>Upload your documents below. CV and degree are required.</p>
              {[
                { key: "cv", label: "CV / Resume *", type: "cv" },
                { key: "id", label: "ID Document", type: "id" },
                { key: "degree", label: "Degree / Qualification *", type: "degree" },
                { key: "hpcsa", label: "HPCSA Certificate", type: "hpcsa" },
              ].map(({ key, label, type }) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, color: BRAND.textLight, marginBottom: 6 }}>{label}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => handleDocUpload(type, e.target.files[0])} style={{ fontSize: 13, flex: 1 }} />
                    {uploadStatus[key] === "uploading" && <span style={{ fontSize: 12, color: "#888" }}>Uploading…</span>}
                    {uploadStatus[key] === "done" && <span style={{ fontSize: 12, color: "#2e7d32" }}>✓ Uploaded</span>}
                    {uploadStatus[key] === "error" && <span style={{ fontSize: 12, color: "#b71c1c" }}>✗ Error</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 5 — Review */}
          {step === 5 && (
            <div>
              <h3 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 700, color: BRAND.text }}>Review &amp; Submit</h3>
              <div style={{ background: BRAND.primaryBg, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                {[
                  ["Name", `${form.first_name} ${form.last_name}`],
                  ["Email", form.email],
                  ["Position", form.position_applied || "—"],
                  ["HPCSA", form.hpcsa_number || "—"],
                  ["Province", form.province || "—"],
                  ["Phone", form.phone || "—"],
                  ["Scrub size", form.scrub_size || "—"],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${BRAND.border}`, fontSize: 13 }}>
                    <span style={{ color: BRAND.textLight, fontWeight: 600 }}>{label}</span>
                    <span style={{ color: BRAND.text }}>{value}</span>
                  </div>
                ))}
              </div>
              <label style={lbl}>Additional information (optional)<textarea value={form.additional_info} onChange={set("additional_info")} rows={3} style={{ ...inp, resize: "vertical" }} /></label>
            </div>
          )}

          {error && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 8, padding: "10px 12px", fontSize: 13, marginTop: 12 }}>{error}</div>}

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            {step > 0 && (
              <button onClick={() => setStep((p) => p - 1)} style={{ ...btnS, width: "auto", padding: "12px 20px" }}>← Back</button>
            )}
            {step < STEPS.length - 1 ? (
              <button onClick={nextStep} disabled={loading} style={btnP(loading)}>{loading ? "Saving…" : "Continue →"}</button>
            ) : (
              <button onClick={finish} disabled={loading} style={btnP(loading)}>{loading ? "Submitting…" : "Submit Application ✓"}</button>
            )}
          </div>
        </div>

        <p style={{ textAlign: "center", marginTop: 14, fontSize: 12, color: BRAND.textLight }}>
          Already registered? <button onClick={onGoLogin} style={{ background: "none", border: "none", color: BRAND.primary, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Sign in here</button>
        </p>
      </div>
    </div>
  );
}

// ─── Onboarding Stepper ───────────────────────────────────────────────────────
// The 6-stage checklist that starts once the candidate signs their Offer
// Letter. onboarding_stage / needs_cellphone_policy live on the Applicant
// record but aren't returned by /api/employees/me/status, so this reads
// them off the existing /api/applicants/profile fetch instead.

function OnboardingStepper({ onboardingStage, needsCellphonePolicy, docsData }) {
  if (!onboardingStage) return null; // offer letter not yet signed — nothing to show

  const stages = ONBOARDING_STAGES.filter((s) => needsCellphonePolicy !== false || s.key !== "cellphone_policy");

  const completedMap = {
    team_member_info: !!docsData?.team_member_info?.submitted_at,
    criminal_check: !!docsData?.criminal_checks?.[0]?.signed_at,
    fixed_term_contract: !!docsData?.employment_contracts?.[0]?.signed_at,
    pledge_confidentiality: !!docsData?.pledge_confidentiality?.[0]?.signed_at,
    cellphone_policy: !!docsData?.cellphone_policies?.[0]?.signed_at,
  };

  const renderAction = () => {
    if (onboardingStage === "complete") {
      return <p style={{ margin: 0, color: BRAND.primary, fontWeight: 700 }}>🎉 Onboarding complete!</p>;
    }
    if (onboardingStage === "team_member_info") {
      return (
        <a href={`${BASENAME}/portal/team-member-info`} style={{ ...btnP(false), display: "inline-block", textAlign: "center", textDecoration: "none", width: "auto", padding: "10px 20px" }}>
          Fill in Team Member Info
        </a>
      );
    }
    if (onboardingStage === "fixed_term_contract") {
      return <p style={{ margin: 0, color: BRAND.textLight, fontSize: 13 }}>Your admin will send your Fixed-Term Employment Contract shortly.</p>;
    }
    if (["criminal_check", "pledge_confidentiality", "cellphone_policy"].includes(onboardingStage)) {
      const recordListKey = onboardingStage === "criminal_check" ? "criminal_checks"
        : onboardingStage === "pledge_confidentiality" ? "pledge_confidentiality" : "cellphone_policies";
      const record = docsData?.[recordListKey]?.[0];
      if (record?.signing_url) {
        return (
          <a href={record.signing_url} style={{ color: "#e67e22", fontWeight: 700, textDecoration: "none", fontSize: 14 }}>
            ⏳ Review and Sign
          </a>
        );
      }
      return <p style={{ margin: 0, color: BRAND.textLight, fontSize: 13 }}>Your admin will send this document shortly.</p>;
    }
    return null;
  };

  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: BRAND.text }}>Onboarding</h3>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        {stages.map((s, i) => {
          const done = completedMap[s.key] || onboardingStage === "complete";
          const current = s.key === onboardingStage;
          const color = done ? BRAND.green : current ? BRAND.primary : "#bbb";
          return (
            <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{
                width: 22, height: 22, minWidth: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, color: "#fff",
                background: done ? BRAND.green : current ? BRAND.primary : "#ddd",
              }}>
                {done ? "✓" : i + 1}
              </span>
              <span style={{ fontSize: 12, fontWeight: current ? 700 : 400, color }}>{s.label}</span>
              {i < stages.length - 1 && <span style={{ color: "#ddd", margin: "0 2px" }}>›</span>}
            </div>
          );
        })}
      </div>
      {renderAction()}
    </div>
  );
}

const cardStyle = { background: "#fff", borderRadius: 14, padding: 20, border: `1px solid ${BRAND.border}` };

// ─── Edit My Details Modal ──────────────────────────────────────────────────

function EditProfileModal({ profile, onClose, onSaved }) {
  const [form, setForm] = useState({
    phone: profile?.phone || "",
    address: profile?.address || "",
    city: profile?.city || "",
    province: profile?.province || "",
    postal_code: profile?.postal_code || "",
    id_number: profile?.id_number || "",
    date_of_birth: profile?.date_of_birth ? profile.date_of_birth.slice(0, 10) : "",
    gender: profile?.gender || "",
    position_applied: profile?.position_applied || "",
    hpcsa_number: profile?.hpcsa_number || "",
    years_experience: profile?.years_experience ?? "",
    current_employer: profile?.current_employer || "",
    employment_type: profile?.employment_type || "",
    hourly_rate: profile?.hourly_rate || "",
    notice_period: profile?.notice_period || "",
    salary_expectation: profile?.salary_expectation || "",
    scrub_size: profile?.scrub_size || "",
    available_from: profile?.available_from ? profile.available_from.slice(0, 10) : "",
    relocation_preferences: profile?.relocation_preferences || "",
    additional_info: profile?.additional_info || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const save = async () => {
    setSaving(true); setError("");
    try {
      // Omit blank optional fields rather than sending "" — e.g. years_experience
      // is an integer column and an empty string fails on save.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { if (v !== "") payload[k] = v; });
      const res = await fetch(`${API_BASE}/api/applicants/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${portalToken()}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(extractError(data, "Could not save your details. Please try again."));
      onSaved(data.applicant);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 1000 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", fontFamily: "system-ui, sans-serif" }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 800, color: BRAND.text }}>Edit My Details</h3>

        <label style={lbl}>ID / Passport number<input value={form.id_number} onChange={set("id_number")} style={inp} /></label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={lbl}>Date of birth<input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} style={inp} /></label>
          <label style={lbl}>Gender<select value={form.gender} onChange={set("gender")} style={inp}><option value="">— Select —</option><option>Male</option><option>Female</option></select></label>
        </div>
        <label style={lbl}>Phone number<input type="tel" value={form.phone} onChange={set("phone")} style={inp} /></label>

        <label style={lbl}>Position applied for<select value={form.position_applied} onChange={set("position_applied")} style={inp}><option value="">— Select —</option>{POSITIONS.map((p) => <option key={p}>{p}</option>)}</select></label>
        <label style={lbl}>HPCSA number<input value={form.hpcsa_number} onChange={set("hpcsa_number")} style={inp} /></label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={lbl}>Years experience<input type="number" value={form.years_experience} onChange={set("years_experience")} style={inp} min={0} /></label>
          <label style={lbl}>Scrub size<select value={form.scrub_size} onChange={set("scrub_size")} style={inp}><option value="">— Select —</option>{SCRUB_SIZES.map((s) => <option key={s}>{s}</option>)}</select></label>
        </div>
        <label style={lbl}>Employment type
          <select value={form.employment_type} onChange={set("employment_type")} style={inp}>
            <option value="">— Select —</option>
            <option value="permanent">Permanent Employment</option>
            <option value="fixed_term">Fixed Term</option>
            <option value="locum">Locum</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label style={lbl}>Current employer<input value={form.current_employer} onChange={set("current_employer")} style={inp} /></label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={lbl}>Notice period<input value={form.notice_period} onChange={set("notice_period")} style={inp} placeholder="e.g. 1 month" /></label>
          {form.employment_type === "locum" ? (
            <label style={lbl}>Rate per hour<input value={form.hourly_rate} onChange={set("hourly_rate")} style={inp} placeholder="e.g. R250/hr" /></label>
          ) : (
            <label style={lbl}>Salary expectation<input value={form.salary_expectation} onChange={set("salary_expectation")} style={inp} placeholder="e.g. R25 000 pm" /></label>
          )}
        </div>
        <label style={lbl}>Available from<input type="date" value={form.available_from} onChange={set("available_from")} style={inp} /></label>

        <label style={lbl}>Street address<input value={form.address} onChange={set("address")} style={inp} /></label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={lbl}>City / Suburb<input value={form.city} onChange={set("city")} style={inp} /></label>
          <label style={lbl}>Postal code<input value={form.postal_code} onChange={set("postal_code")} style={inp} /></label>
        </div>
        <label style={lbl}>Province<select value={form.province} onChange={set("province")} style={inp}><option value="">— Select —</option>{PROVINCES.map((p) => <option key={p}>{p}</option>)}</select></label>
        <label style={lbl}>Relocation preferences<input value={form.relocation_preferences} onChange={set("relocation_preferences")} style={inp} /></label>
        <label style={lbl}>Additional information<textarea value={form.additional_info} onChange={set("additional_info")} rows={3} style={{ ...inp, resize: "vertical" }} /></label>

        {error && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 8, padding: "10px 12px", fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button onClick={onClose} disabled={saving} style={{ ...btnS, width: "auto", padding: "12px 20px" }}>Cancel</button>
          <button onClick={save} disabled={saving} style={btnP(saving)}>{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Applicant Dashboard ──────────────────────────────────────────────────────

function ApplicantDashboard({ user, onLogout }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docsData, setDocsData] = useState(null);
  const [profileInfo, setProfileInfo] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);

  const loadStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/employees/me/status`, { headers: { Authorization: `Bearer ${portalToken()}` } });
      const data = await res.json();
      setStatus(data);
    } catch { /* silent */ }
  };

  const loadProfile = async () => {
    try {
      // onboarding_stage / needs_cellphone_policy live on the Applicant
      // record — /api/applicants/profile returns it directly.
      const res = await fetch(`${API_BASE}/api/applicants/profile`, { headers: { Authorization: `Bearer ${portalToken()}` } });
      const data = await res.json();
      if (res.ok) setProfileInfo(data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => { await loadStatus(); setLoading(false); })();
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/applicants/documents`, { headers: { Authorization: `Bearer ${portalToken()}` } });
        const data = await res.json();
        if (res.ok) setDocsData(data);
      } catch { /* silent */ }
    })();
    loadProfile();
  }, []);

  const handleProfileSaved = (updatedApplicant) => {
    setProfileInfo(updatedApplicant);
    setShowEdit(false);
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 4000);
    loadStatus(); // status.application mirrors the same Applicant record
  };

  const downloadDocument = async (docId, filename) => {
    try {
      setDownloadingId(docId);
      const res = await fetch(`${API_BASE}/api/documents/${docId}/download`, { headers: { Authorization: `Bearer ${portalToken()}` } });
      if (!res.ok) throw new Error("Could not download file");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "document.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Could not download this file. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const appStatus = status?.application?.application_review_status || "Under review";
  const sd = STATUS_DISPLAY[appStatus] || STATUS_DISPLAY["Under review"];

  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, fontFamily: "system-ui, sans-serif" }}>
      {/* Nav */}
      <div style={{ background: BRAND.primary, padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 18, letterSpacing: "-0.3px" }}>guud vision</div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>{user?.name}</span>
          <a href="/portal/how-it-works" style={{ color: "rgba(255,255,255,0.9)", fontSize: 13, textDecoration: "none", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 6, padding: "6px 14px" }}>
            What to Expect
          </a>
          <button onClick={onLogout} style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 13 }}>Sign Out</button>
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "28px 16px" }}>
        {loading ? (
          <p style={{ textAlign: "center", color: "#888" }}>Loading your application…</p>
        ) : (
          <>
            {/* Status card */}
            <div style={{ background: sd.bg, border: `2px solid ${sd.color}44`, borderRadius: 16, padding: 24, marginBottom: 20, textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>{sd.icon}</div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: sd.color, marginBottom: 4 }}>Application Status</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: sd.color }}>{sd.label}</div>
              {status?.application?.folder_number && (
                <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Folder: {status.application.folder_number}</div>
              )}
            </div>

            {savedMsg && (
              <div style={{ background: "#f1f8e9", color: "#2e7d32", border: "1px solid #a5d6a7", borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 16, textAlign: "center" }}>
                ✓ Your details have been saved.
              </div>
            )}

            {/* Details */}
            {status?.application && (
              <div style={{ background: "#fff", borderRadius: 14, padding: 20, marginBottom: 16, border: `1px solid ${BRAND.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: BRAND.text }}>Your Application Details</h3>
                  <button
                    onClick={() => setShowEdit(true)}
                    style={{ background: "none", border: `1.5px solid ${BRAND.primary}`, color: BRAND.primary, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                  >
                    ✎ Edit My Details
                  </button>
                </div>
                {[
                  ["Position", status.application.applicant_role],
                  ["HPCSA Number", status.application.practitioner_number],
                  ["Province", status.application.current_province],
                  ["Availability", status.application.availability_status],
                  ["Available From", status.application.available_from ? new Date(status.application.available_from).toLocaleDateString("en-ZA") : null],
                  ["Contact", status.application.contact_details],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${BRAND.border}`, fontSize: 13 }}>
                    <span style={{ color: BRAND.textLight, fontWeight: 600 }}>{label}</span>
                    <span style={{ color: BRAND.text }}>{value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Placement info if placed */}
            {appStatus === "Placed" && status?.application?.fleet_nr && (
              <div style={{ background: "#e0f2f1", border: "1px solid #80cbc4", borderRadius: 14, padding: 20, marginBottom: 16 }}>
                <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700, color: "#00695c" }}>Current Placement</h3>
                {[
                  ["Fleet / Mobile", status.application.fleet_nr],
                  ["Project", status.application.project],
                  ["Area", status.application.area],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #b2dfdb", fontSize: 13 }}>
                    <span style={{ color: "#004d40", fontWeight: 600 }}>{label}</span>
                    <span style={{ color: "#00695c" }}>{value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Onboarding — 6-stage checklist that starts once the Offer Letter is signed */}
            <OnboardingStepper
              onboardingStage={profileInfo?.onboarding_stage}
              needsCellphonePolicy={profileInfo?.needs_cellphone_policy}
              docsData={docsData}
            />

            {/* My Documents — offer letter, contract, and everything uploaded, in one place */}
            {docsData && (docsData.offer_letters?.length > 0 || docsData.employment_contracts?.length > 0 || docsData.documents?.length > 0) && (
              <div style={{ background: "#fff", borderRadius: 14, padding: 20, marginBottom: 16, border: `1px solid ${BRAND.border}` }}>
                <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: BRAND.text }}>My Documents</h3>

                {docsData.offer_letters?.map((o) => (
                  <div key={`offer-${o.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${BRAND.border}`, fontSize: 13 }}>
                    <span style={{ color: BRAND.text }}>📄 Offer Letter</span>
                    {o.signed_at ? (
                      <button
                        onClick={() => downloadDocument(o.signed_document_id, "Signed_Offer_Letter.pdf")}
                        disabled={downloadingId === o.signed_document_id}
                        style={{ background: "none", border: "none", color: BRAND.primary, fontWeight: 700, cursor: "pointer", fontSize: 13 }}
                      >
                        ✅ Signed — Download
                      </button>
                    ) : (
                      <a href={o.signing_url} style={{ color: "#e67e22", fontWeight: 700, textDecoration: "none" }}>
                        ⏳ Awaiting your signature — Sign Now
                      </a>
                    )}
                  </div>
                ))}

                {docsData.employment_contracts?.map((c) => (
                  <div key={`contract-${c.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${BRAND.border}`, fontSize: 13 }}>
                    <span style={{ color: BRAND.text }}>📝 Employment Contract</span>
                    {c.signed_at ? (
                      <button
                        onClick={() => downloadDocument(c.signed_document_id, "Signed_Employment_Contract.pdf")}
                        disabled={downloadingId === c.signed_document_id}
                        style={{ background: "none", border: "none", color: BRAND.primary, fontWeight: 700, cursor: "pointer", fontSize: 13 }}
                      >
                        ✅ Signed — Download
                      </button>
                    ) : (
                      <a href={c.signing_url} style={{ color: "#e67e22", fontWeight: 700, textDecoration: "none" }}>
                        ⏳ Awaiting your signature — Sign Now
                      </a>
                    )}
                  </div>
                ))}

                {docsData.documents?.map((d) => (
                  <div key={`doc-${d.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${BRAND.border}`, fontSize: 13 }}>
                    <span style={{ color: BRAND.text }}>📎 {d.filename}</span>
                    <button
                      onClick={() => downloadDocument(d.id, d.filename)}
                      disabled={downloadingId === d.id}
                      style={{ background: "none", border: "none", color: BRAND.primary, fontWeight: 700, cursor: "pointer", fontSize: 13 }}
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}

            <p style={{ textAlign: "center", fontSize: 12, color: BRAND.textLight, marginTop: 20 }}>
              Questions? <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: BRAND.primary }}>{CONTACT_EMAIL}</a>
            </p>
          </>
        )}
      </div>

      {showEdit && (
        <EditProfileModal
          profile={profileInfo}
          onClose={() => setShowEdit(false)}
          onSaved={handleProfileSaved}
        />
      )}
    </div>
  );
}

// ─── Main Portal ──────────────────────────────────────────────────────────────

// When the stored login token expires (ms since epoch), or null if unreadable.
// Logins last 12h; an expired one goes back to the sign-in screen.
const tokenExpiresAt = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.exp ? payload.exp * 1000 : null;
  } catch { return null; }
};

export default function ApplicantPortal() {
  const [view, setView] = useState(() => {
    const tkn = localStorage.getItem("applicantToken");
    const usr = localStorage.getItem("applicantUser");
    const expiresAt = tkn ? tokenExpiresAt(tkn) : null;
    if (expiresAt && expiresAt <= Date.now()) {
      localStorage.removeItem("applicantToken");
      localStorage.removeItem("applicantUser");
      return "login";
    }
    return tkn && usr ? "dashboard" : "login";
  });
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("applicantUser") || "null"); } catch { return null; }
  });
  const [showPOPIA, setShowPOPIA] = useState(false);
  const [popiaAccepted, setPopiaAccepted] = useState(false);
  const [declinedPOPIA, setDeclinedPOPIA] = useState(false);

  useEffect(() => {
    if (window.location.pathname.includes("/apply")) {
      if (!popiaAccepted) setShowPOPIA(true);
      else setView("register");
    }
  }, [popiaAccepted]);

  const handleLogin = (u) => { setUser(u); setView("dashboard"); };
  const handleRegistered = (u) => { setUser(u); setView("dashboard"); };
  const handleLogout = () => {
    localStorage.removeItem("applicantToken");
    localStorage.removeItem("applicantUser");
    setUser(null); setView("login");
  };

  // Sign out when the login token expires while the portal is open.
  useEffect(() => {
    if (view !== "dashboard") return undefined;
    const expiresAt = tokenExpiresAt(localStorage.getItem("applicantToken") || "");
    if (!expiresAt) return undefined;
    const timer = setTimeout(handleLogout, Math.max(expiresAt - Date.now(), 0));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);
  const handleGoRegister = () => {
    if (popiaAccepted) setView("register");
    else setShowPOPIA(true);
  };

  return (
    <>
      {showPOPIA && (
        <POPIAModal
          onAccept={() => { setPopiaAccepted(true); setShowPOPIA(false); setView("register"); setDeclinedPOPIA(false); }}
          onDecline={() => { setDeclinedPOPIA(true); setShowPOPIA(false); }}
        />
      )}
      {view === "dashboard" && user && <ApplicantDashboard user={user} onLogout={handleLogout} />}
      {view === "register" && <RegisterPage onRegistered={handleRegistered} onGoLogin={() => setView("login")} />}
      {view === "login" && (
        <LoginPage
          onLogin={handleLogin}
          onGoRegister={handleGoRegister}
          declinedNotice={declinedPOPIA}
        />
      )}
    </>
  );
}
